import { useState } from 'react';

import type { Device, Connection, ApplicationEntity, AcceptedCallingAeTitle } from '../types/dicom';

export const useDeviceManagement = (initialDevices: Device[]) => {
  const [devices, setDevices] = useState<Device[]>(initialDevices);

  const handleDeviceAction = async (deviceId: string, action: string) => {
    setDevices((prev) =>
      prev.map((device) => {
        if (device.id === deviceId) {
          switch (action) {
            case 'start':
              return { ...device, status: 'starting' as const };
            case 'stop':
              return { ...device, status: 'stopping' as const };
            case 'restart':
              return { ...device, status: 'starting' as const };
            default:
              return device;
          }
        }
        return device;
      })
    );

    // Simulate async operation
    setTimeout(() => {
      if (action === 'start' || action === 'restart') {
        setDevices((prev) =>
          prev.map((device) =>
            device.id === deviceId
              ? { ...device, status: 'active' as const }
              : device
          )
        );
      } else if (action === 'stop') {
        setDevices((prev) =>
          prev.map((device) =>
            device.id === deviceId
              ? { ...device, status: 'inactive' as const }
              : device
          )
        );
      }
    }, 2000);
  };

  const handleCloneDevice = async (device: Device, newName: string) => {
    const clonedDevice: Device = {
      ...device,
      id: `device-${Date.now()}`,
      name: newName,
      status: 'inactive',
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      connections: device.connections.map((conn) => ({
        ...conn,
        id: `conn-${Date.now()}-${Math.random()}`,
        status: 'disconnected' as const,
        applicationEntities: conn.applicationEntities.map((ae) => ({
          ...ae,
          id: `ae-${Date.now()}-${Math.random()}`,
          status: 'stopped' as const,
          createdAt: new Date().toISOString(),
        })),
      })),
    };

    setDevices((prev) => [...prev, clonedDevice]);
    return clonedDevice;
  };

  const handleAEAction = async (
    deviceId: string,
    connectionId: string,
    aeId: string,
    action: string
  ) => {
    setDevices((prev) =>
      prev.map((device) => {
        if (device.id === deviceId) {
          return {
            ...device,
            connections: device.connections.map((conn) => {
              if (conn.id === connectionId) {
                return {
                  ...conn,
                  applicationEntities: conn.applicationEntities.map((ae) => {
                    if (ae.id === aeId) {
                      switch (action) {
                        case 'start':
                          return { ...ae, status: 'starting' as const };
                        case 'stop':
                          return { ...ae, status: 'stopped' as const };
                        case 'restart':
                          return { ...ae, status: 'starting' as const };
                        default:
                          return ae;
                      }
                    }
                    return ae;
                  }),
                };
              }
              return conn;
            }),
          };
        }
        return device;
      })
    );

    // Simulate async operation
    setTimeout(() => {
      if (action === 'start' || action === 'restart') {
        setDevices((prev) =>
          prev.map((device) => {
            if (device.id === deviceId) {
              return {
                ...device,
                connections: device.connections.map((conn) => {
                  if (conn.id === connectionId) {
                    return {
                      ...conn,
                      applicationEntities: conn.applicationEntities.map((ae) =>
                        ae.id === aeId
                          ? { ...ae, status: 'running' as const }
                          : ae
                      ),
                    };
                  }
                  return conn;
                }),
              };
            }
            return device;
          })
        );
      }
    }, 2000);
  };

  const handleAddConnection = async (device: Device, connectionData: any) => {
    const newConnection: Connection = {
      id: `conn-${Date.now()}`,
      name: connectionData.name,
      hostname: connectionData.hostname,
      port: Number.parseInt(connectionData.port) || 11112,
      protocol: 'TCP',
      description: connectionData.description,
      tlsEnabled: connectionData.tlsEnabled || false,
      tlsNeedClientAuth: connectionData.tlsNeedClientAuth || false,
      applicationEntities: [],
    };

    setDevices((prev) =>
      prev.map((d) =>
        d.id === device.id
          ? { ...d, connections: [...d.connections, newConnection] }
          : d
      )
    );

    return newConnection;
  };

  const handleConfigureDevice = async (device: Device, formData: any) => {
    setDevices((prev) =>
      prev.map((d) =>
        d.id === device.id
          ? {
              ...d,
              name: formData.deviceName,
              description: formData.description,
              manufacturer: formData.manufacturer,
              model: formData.model,
              stationName: formData.stationName,
              institutionName: formData.institutionName,
              institutionalDepartmentName: formData.institutionalDepartmentName,
              lastUpdated: new Date().toISOString(),
            }
          : d
      )
    );
  };

  const handleConfigureAE = async (
    device: Device,
    connection: Connection,
    ae: ApplicationEntity,
    formData: any
  ) => {
    setDevices((prev) =>
      prev.map((d) => {
        if (d.id === device.id) {
          return {
            ...d,
            connections: d.connections.map((conn) => {
              if (conn.id === connection.id) {
                return {
                  ...conn,
                  applicationEntities: conn.applicationEntities.map((entity) =>
                    entity.id === ae.id
                      ? {
                          ...entity,
                          aeTitle: formData.aeTitle,
                          description: formData.description,
                          dicomType: formData.dicomType,
                        }
                      : entity
                  ),
                };
              }
              return conn;
            }),
          };
        }
        return d;
      })
    );
  };

  const handleDeleteConnection = async (
    deviceId: string,
    connectionId: string
  ) => {
    setDevices((prev) =>
      prev.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              connections: device.connections.filter(
                (conn) => conn.id !== connectionId
              ),
            }
          : device
      )
    );
  };

  const handleStopAllAEs = async (deviceId: string, connectionId: string) => {
    setDevices((prev) =>
      prev.map((device) => {
        if (device.id === deviceId) {
          return {
            ...device,
            connections: device.connections.map((conn) => {
              if (conn.id === connectionId) {
                return {
                  ...conn,
                  applicationEntities: conn.applicationEntities.map((ae) => ({
                    ...ae,
                    status: 'stopped' as const,
                  })),
                };
              }
              return conn;
            }),
          };
        }
        return device;
      })
    );
  };

  const updateAESyntaxConfig = (
    deviceId: string,
    connectionId: string,
    aeId: string,
    syntaxConfig: any
  ) => {
    setDevices((prev) =>
      prev.map((d) => {
        if (d.id === deviceId) {
          return {
            ...d,
            connections: d.connections.map((conn) => {
              if (conn.id === connectionId) {
                return {
                  ...conn,
                  applicationEntities: conn.applicationEntities.map((entity) =>
                    entity.id === aeId ? { ...entity, syntaxConfig } : entity
                  ),
                };
              }
              return conn;
            }),
          };
        }
        return d;
      })
    );
  };

  const updateAcceptedCallingAETitles = (
    deviceId: string,
    connectionId: string,
    aeId: string,
    acceptedTitles: AcceptedCallingAeTitle[]
  ) => {
    setDevices((prev) =>
      prev.map((d) => {
        if (d.id === deviceId) {
          return {
            ...d,
            connections: d.connections.map((conn) => {
              if (conn.id === connectionId) {
                return {
                  ...conn,
                  applicationEntities: conn.applicationEntities.map((entity) =>
                    entity.id === aeId 
                      ? { ...entity, acceptedCallingAeTitles: acceptedTitles } 
                      : entity
                  ),
                };
              }
              return conn;
            }),
          };
        }
        return d;
      })
    );
  };

  return {
    devices,
    setDevices,
    handleDeviceAction,
    handleCloneDevice,
    handleAEAction,
    handleAddConnection,
    handleConfigureDevice,
    handleConfigureAE,
    handleDeleteConnection,
    handleStopAllAEs,
    updateAESyntaxConfig,
    updateAcceptedCallingAETitles,
  };
};
